import type { VoidFunctionComponent } from 'react';

import { Action, ResourceType } from '../../../../../typings/permissions';
import { window } from '../../../../globals';
import { useHasPermissionForTeamMembers } from '../../../../helpers/permissions';
import useHasPermission from '../../../../shared/permissions/useHasPermission';
import { icons, IconText } from '../../../generic';
import { MenuItem as GenericMenuItem, Menu, MenuButton } from '../../../generic/Menu';
import Tooltip from '../../../generic/Tooltip';
import type { SelectOption } from '../../../../types';

import useSelectRoles from './hooks/useSelectRoles';

import styles from './TeamSettings.module.less';

//  Trying to keep window references outside of the component whenever possible.
const getCurrentUserId = () => window.user?.id;

type ComponentProps = {
  /** The ID of the user to select the role for */
  userId: number;
  /** If it's disabled, this is due to permission issues */
  isDisabled: boolean;
  /** If it's the current user, we need to show a different tooltip */
  isCurrentUser: boolean;
  /** The role options to display in the dropdown */
  roleOptions: Array<SelectOption<number>>;
  /** The currently selected role */
  selectedRole: SelectOption<number> | null;
  /** The function to call when the role changes */
  onChangeRole: (role: SelectOption<number>) => void;
};

/**
 * This is a dumb component which renders a styled DropdownButton, allowing
 * the user to select the role for a given user on a team/in a team group.
 */
const TeamRoleSelector: VoidFunctionComponent<ComponentProps> = ({
  userId,
  isDisabled,
  isCurrentUser,
  roleOptions,
  selectedRole,
  onChangeRole,
}) => {
  //  Note we call .toString() here mostly for type safety, since technically SelectOption['label] could be a ReactNode.
  const selectedRoleLabel = selectedRole?.label?.toString() ?? 'None';
  if (isDisabled) {
    return (
      <Tooltip
        id={`team-role-select-tooltip-${userId}`}
        tooltip={`You are not permitted to ${isCurrentUser ? 'change your own role in a team.' : 'manage users on this team.'}`}
      >
        <span className="inline-block">
          {selectedRoleLabel}
        </span>
      </Tooltip>
    );
  }

  return (
    <Menu
      menuButton={
        <MenuButton className={styles.teamRoleSelector}>
          <IconText
            icon={icons.chevronDown}
            alignIcon="right"
            text={selectedRoleLabel}
          />
        </MenuButton>
      }
      direction="bottom"
      id={`team-role-select-dropdown-${userId}`}
    >
      {roleOptions.map(({ value: roleId, label }) => (
        <GenericMenuItem
          key={roleId}
          checked={roleId === selectedRole?.value}
          onClick={() => onChangeRole({ value: roleId, label })}
        >
          {label}
        </GenericMenuItem>
      ))}
    </Menu>
  );
};

type ContainerProps = {
  /** ID of the user to select the role for */
  userId: number;
  /** The current team/team group ID. Used for permission checking. */
  teamId: number;
  /** The initial/currently selected role ID. Optional because it might not be set yet. */
  roleId?: number;
  /** Whether the role selector is disabled */
  isDisabled?: boolean;
  /**
   * The function to call when the role changes.
   * @param {number} userId - The ID of the user whose role is being changed
   * @param {number} roleId - The ID of the new role being assigned
   * @returns {Promise<void> | void} A promise that resolves when the role change is complete
   */
  handleChangeRole: (userId: number, roleId: number) => Promise<void> | void;
};

/**
 * Container component which connects the TeamRoleSelector up with
 * some state, specifically it pulls in the Roles options and determines
 * if the current user is allowed to manage the given user's role.
 */
const TeamRoleSelectorContainer: VoidFunctionComponent<ContainerProps> = ({
  userId,
  roleId,
  teamId,
  isDisabled,
  handleChangeRole,
}) => {
  //  Permissions checks
  const hasPermission = useHasPermission();
  const isCurrentUser = userId === getCurrentUserId();
  const canManageRoles = hasPermission({ resourceType: ResourceType.Role, action: Action.Manage });
  const hasPermissionForTeamUsers = useHasPermissionForTeamMembers();
  const canEditTeamUsers = hasPermissionForTeamUsers(Action.Edit, teamId);
  const doesNotHavePermission = (isCurrentUser && !canManageRoles) || !canEditTeamUsers;

  //  Role options
  const {
    roleOptions,
    isLoading,
    selectedRole,
    onChangeRole,
  } = useSelectRoles({ selectedRoleId: roleId, teamId });

  const onRoleChange = (role: SelectOption<number>) => {
    //  Update the local state, so that the UI updates immediately.
    onChangeRole(role);
    //  Call the parent function to update the team/role in the database.
    handleChangeRole(userId, role.value);
  };

  return (
    <TeamRoleSelector
      userId={userId}
      isDisabled={isLoading || doesNotHavePermission || !!isDisabled}
      isCurrentUser={isCurrentUser}
      roleOptions={roleOptions}
      selectedRole={selectedRole}
      onChangeRole={onRoleChange}
    />
  );
};

export default TeamRoleSelectorContainer;
